import { Injectable } from '@angular/core';
import { ApplicantService } from '@core/services/auth-user/applicant.service';
import { MixpanelService } from '@core/services/mixpanel.service';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { StatusService } from '@core/services/status.service';
import { TokenService } from '@core/services/token/token.service';
import { TranslationService } from '@core/services/translation.service';
import { Applicant } from '@core/typings/applicant.typing';
import { UpdateUserProfile, User } from '@core/typings/client-user.typing';
import { PortalTypes } from '@core/typings/token.typing';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { CustomDataTablesService } from '@features/custom-data-tables/services/custom-data-table.service';
import { EmployeeSSOFieldsService } from '@features/employee-sso-fields/employee-sso-fields.service';
import { AdHocReportingDefinitions } from '@features/reporting/services/ad-hoc-reporting-definitions.service';
import { UserService } from '@features/users/user.service';
import { I18nService } from '@yourcause/common/i18n';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { ClientUserResources } from './client-user.resources';
import { ClientUserState } from './client-user.state';
import { HeapService, TrackingPropertyNames } from '@yourcause/common/heap';

@AttachYCState(ClientUserState)
@Injectable({ providedIn: 'root' })
export class ClientUserService extends BaseYCService<ClientUserState> {

   constructor (
    private clientUserResources: ClientUserResources,
    private userService: UserService,
    private i18nService: I18nService,
    private determinationService: PortalDeterminationService,
    private customDataTableService: CustomDataTablesService,
    private translationService: TranslationService,
    private adHocDefinitions: AdHocReportingDefinitions,
    private statusService: StatusService,
    private employeeSSOFieldService: EmployeeSSOFieldsService,
    private mixpanel: MixpanelService,
    private tokenService: TokenService,
    private portal: PortalDeterminationService,
    private clientSettingsService: ClientSettingsService,
    private applicantService: ApplicantService,
    private heapService: HeapService
  ) {
    super();

    i18nService.language$.subscribe(async (culture) => {
      document.getElementsByTagName('html')[0].lang = this.i18nService.simpleLanguage || 'en';
      if (this.determinationService.isManager) {
        if (this.userService.user) {
          if (this.userService.user.culture !== culture) {
            if (!this.tokenService.hasImpersonationToken()) {
              await this.updateProfile({
                firstName: (this.userService.user.firstName || '').trim(),
                lastName: (this.userService.user.lastName || '').trim(),
                culture
              });
            }
            this.customDataTableService.resetCustomDataTableOptionsMap();
            this.statusService.reset();
            this.adHocDefinitions.reset();
            this.userService.setUser({
              ...this.userService.user,
              culture
            });
            await Promise.all([
              this.translationService.resetViewTranslations(),
              this.employeeSSOFieldService.resetEmployeeSSOFields()
            ]);

            this.heapService.removeEventPropertyAndAssignNewValue(TrackingPropertyNames.Language, culture);
          }
        }
      }
    });
  }

  get cultureToSaveAfterLogin () {
    return this.get('cultureToSaveAfterLogin');
  }

  get browserLocaleToSaveAfterLogin () {
    return this.get('browserLocaleToSaveAfterLogin');
  }

  setBrowserLocaleToSaveAfterLogin (lang: string) {
    this.set('browserLocaleToSaveAfterLogin', lang);
  }

  setCultureToSaveAfterLogin (lang: string) {
    this.set('cultureToSaveAfterLogin', lang);
  }

  async getUser (force: boolean): Promise<User> {
    if (!force && this.userService.get('user')) {
      return this.userService.user;
    }
    const user = await this.clientUserResources.getUser();
    if (!this.tokenService.hasImpersonationToken()) {
      this.mixpanel.setProfile({
        [TrackingPropertyNames.Email]: user.email,
        'First Name': user.firstName,
        'Last Name': user.lastName,
        'Portal Type': 'GrantManager'
      });
    }
    let cultureToSet = user?.culture;
    if (
      user &&
      user.culture &&
      user.culture !== this.i18nService.language
    ) {
      const userSelectedLang = this.cultureToSaveAfterLogin;
      const browserSelectedLang = this.browserLocaleToSaveAfterLogin;
      // Only override to the browser locale if the user is still on the default language (en-US)
      // If the applicant has a custom language, we should respect that and ignore the browser lang
      const shouldUpdateLanguage = !!userSelectedLang ||
        (
          !!browserSelectedLang && (user.culture === this.i18nService.DEFAULT_LANG)
        );
      if (shouldUpdateLanguage) {
        cultureToSet = userSelectedLang || browserSelectedLang;
        await this.updateProfile({
          firstName: user.firstName,
          lastName: user.lastName,
          culture: cultureToSet
        });
        this.setCultureToSaveAfterLogin(undefined);
        this.setBrowserLocaleToSaveAfterLogin(undefined);
      }
    }
    this.i18nService.setLanguage(cultureToSet);

    this.userService.setUser(user);

    return user;
  }

  updateProfile (
    data: Partial<UpdateUserProfile>
  ) {
    return this.clientUserResources.updateProfile(data);
  }

  deactivateAccount () {
    return this.clientUserResources.deactivateAccount();
  }

  async resolveUser () {
    let user: Applicant|User;
    const tokenContent = this.tokenService.parseJwt();

    if (this.portal.isApply) {
      user = await this.applicantService.getApplicant(false);
    } else if (this.portal.isManager) {
      user = await this.getUser(false);
    } else {
      user = this.tokenService.castTokenContentToUser(tokenContent);
      this.userService.setAdmin(user);
      this.userService.setAdminPermissions();
    }
    const currency = this.userService.applicant ?
      this.userService.applicant.lastSelectedCurrency :
      null;
    this.userService.setLastSelectedCurrency(currency);
    this.setAffiliateInfo(
      user.id,
      user.firstName,
      user.lastName,
      user.email,
      tokenContent.impersonated_by_user_id
    );
  }

  async setAffiliateInfo (
    userId: number,
    userFirstName: string,
    userLastName: string,
    userEmail: string,
    impersonated_by_user_id: string
  ) {
    await this.clientSettingsService.setAffiliateInfo();
    const affiliateInfo = this.clientSettingsService.getClientAffiliateInfo(
      this.clientSettingsService?.clientBranding?.name
    );
    if (!!this.clientSettingsService.affiliateInfo) {
      if (this.tokenService.hasImpersonationToken()) {
        this.mixpanel.register({
          ...affiliateInfo,
          'Is Impersonating': 'Yes',
          'Impersonated First Name': userFirstName,
          'Impersonated Last Name': userLastName,
          'Impersonated Email': userEmail
        });
        this.mixpanel.identify(PortalTypes.Admin + '-' +impersonated_by_user_id);
      } else {
        const portalType = this.portal.isApply ?
          PortalTypes.Applicant :
          this.portal.isManager ? PortalTypes.GrantManager : PortalTypes.Admin;
        this.mixpanel.identify(portalType + '-' + userId);
        this.mixpanel.setProfile({
          ...affiliateInfo,
          $first_name: userFirstName,
          $last_name: userLastName,
          $name: userFirstName + ' ' + userLastName,
          $email: userEmail,
          'Portal Type': portalType
        });
      }
    }
  }
}
